import { Box, Divider, List, Typography } from '@mui/material';
import { useBooleanFlag } from '@trm/feature-flags/web';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useGetMenuMaster } from '~/api/menu';
import { useChoosePath } from '~/beta/betaHooks';
import { chosenNavItem } from '~/beta/chosenNavItem';
import useAuth from '~/hooks/useAuth';
import { useEnableTraderProfileAdminFlag } from '~/hooks/useEnableTraderProfileAdminFlag';
import { useTraderProfileModerationFlag } from '~/hooks/useTraderProfileModerationFlag';
import menuItem from '~/menu-items';
// types
import { NavItemType } from '~/types/menu';
import { PagePolicies } from '~/utils/roles';

import NavGroup from './NavGroup';
// project import
import NavItem from './NavItem';

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

const Navigation = () => {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { hasPermission } = useAuth();

  const [selectedItems, setSelectedItems] = useState<string | undefined>('');
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [menuItems, setMenuItems] = useState<{ items: NavItemType[] }>({ items: [] });

  const isBetaPreviewWithoutAccess = useCallback(
    (item: NavItemType) => item.beta?.kind === 'preview' && !hasPermission(PagePolicies.BetaPages),
    [hasPermission],
  );

  const hasAccess = useCallback(
    (item: NavItemType) => {
      const hadChildren = Boolean(item.children);

      // Determine if each child item has access
      if (item.children) {
        item.children = item.children.filter((child) => hasAccess(child));
      }

      // If the item has children and none of them have access, return false
      if (item.children && item.children.length === 0 && hadChildren) {
        return false;
      }

      if (isBetaPreviewWithoutAccess(item)) return false;

      if (item.policies) {
        return hasPermission(item.policies);
      }

      return true;
    },
    [hasPermission, isBetaPreviewWithoutAccess],
  );

  const flags = useFlags();
  const isTrainingCampEnabled = flags['enable-dashboard-training-camp'] ?? false;
  const isCaseManagementEnabled = useBooleanFlag('case-management-system', false);
  const isPurchasesEnabled = useBooleanFlag('admin-purchase-orders', false);
  const { isTraderProfileAdminEnabled } = useEnableTraderProfileAdminFlag();
  const { isTraderProfileModerationEnabled } = useTraderProfileModerationFlag();

  useEffect(() => {
    // Remove items that the user does not have access to
    const items = _.cloneDeep([...menuItem.items]);
    const filteredMenuItems = items.filter((item) => {
      const access = hasAccess(item);
      return access;
    });

    setMenuItems({ items: filteredMenuItems });
    // eslint-disable-next-line
  }, [menuItem, hasPermission]);

  const chooseUrl = chosenNavItem(useChoosePath());

  const filterNavItemsByFlag = (items: NavItemType[]): NavItemType[] => {
    return items
      .map((item) => {
        const children = item.children ? filterNavItemsByFlag(item.children) : undefined;
        if (item.id === 'training-camp' && !isTrainingCampEnabled) return null;
        if (item.id === 'adminCases' && !isCaseManagementEnabled) return null;
        if (item.id === 'adminTraderProfiles' && !isTraderProfileAdminEnabled) return null;
        if (item.id === 'adminTraderProfilesModeration' && !isTraderProfileModerationEnabled)
          return null;
        if (item.id === 'adminPurchaseOrders' && !isPurchasesEnabled) return null;
        return chooseUrl({ ...item, ...(children && { children }) });
      })
      .filter((item): item is NavItemType => item !== null);
  };

  const navGroups = filterNavItemsByFlag(menuItems.items).map((item, index) => {
    switch (item.type) {
      case 'group':
        if (item.url) {
          return (
            <List key={item.id}>
              {index !== 0 && <Divider sx={{ my: 0.5 }} />}
              <NavItem item={item} level={1} isParents />
            </List>
          );
        }

        return (
          <NavGroup
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            item={item}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Navigation Group
          </Typography>
        );
    }
  });

  return (
    <Box
      sx={{
        pt: drawerOpen ? 2 : 0,
        '& > ul:first-of-type': { mt: 0 },
      }}
    >
      {navGroups}
    </Box>
  );
};

export default Navigation;
